import cuid from "cuid";
import { filter, get, map } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { CREATE_ATTACHMENT } from "utils/api/graphql/mutations/attachment";
import { ATTACHMENT_TYPES, KEYS, RELATIONSHIP } from "utils/constants";
import Template from "../Template";
import View from "./View";

export const DOCS_TYPE = {
  healthInsuranceRightsCertificate:
    ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  dependentProof: ATTACHMENT_TYPES.CHILD_DEPENDENT_PROOF,
  bankAccountDetailsForPayment:
    ATTACHMENT_TYPES.BANK_ACCOUNT_DETAILS_FOR_PAYMENT,
};

const ChildrenJustification = ({
  currentStatus,
  tracer,
  project,
  onBack,
  updateUrl,
  onNext,
}) => {
  const [initialValues, setInitialValues] = useState({ includedChildren: [] });

  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_LMF.CHILDREN_JUSTIFICATION", {
    returnObjects: true,
  });

  const getAge = (birthDate) => {
    if (!birthDate) return 0;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);

  const { data: subscription } = useQuery(graphql.queries.SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });

  const children = get(project, "contact.relatedPersons", []).filter(
    ({ relationship }) => relationship === RELATIONSHIP.CHILD,
  );

  const includedChildren = children
    .map((child) => ({
      id: get(child, "identity.id") ?? child.id,
      userId: get(child, "identity.user.id"),
      age: getAge(get(child, "identity.user.birthDate")),
      identity: get(child, "identity"),
    }))
    .filter((c) => c.userId);

  const childrenUserIds = includedChildren.map((child) => child.userId);

  const { loading: attachmentLoading } = useQuery(graphql.queries.ATTACHMENTS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        AND: [
          {
            subscription: { id: subscriptionId },
          },
          {
            OR: childrenUserIds.map((id) => ({ user: { id } })),
          },
        ],
      },
      isIn: {
        type: Object.values(DOCS_TYPE),
      },
    },

    skip: childrenUserIds.length === 0,

    onCompleted: ({ attachments: { data: attachmentsList } }) => {
      const initialVals = {};
      includedChildren.forEach(({ userId, age }) => {
        const childAttachments = attachmentsList.filter((att) => {
          const attachmentUserId = att.user?.id ?? att.userId;
          return attachmentUserId === userId;
        });

        initialVals[`healthInsuranceRightsCertificate_${userId}`] = map(
          filter(
            childAttachments,
            (att) =>
              att.type === ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        );

        initialVals[`bankAccountDetailsForPayment_${userId}`] = map(
          filter(
            childAttachments,
            (att) =>
              att.type === ATTACHMENT_TYPES.BANK_ACCOUNT_DETAILS_FOR_PAYMENT,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        );

        initialVals[`dependentProof_${userId}`] = map(
          filter(
            childAttachments,
            (att) => att.type === ATTACHMENT_TYPES.CHILD_DEPENDENT_PROOF,
          ),
          ({ id, name, fileUrl }) => ({
            uid: id,
            name,
            status: "done",
            url: fileUrl,
          }),
        );
      });

      setInitialValues({ ...initialVals, includedChildren });

      setLoading(false);
    },
  });

  const [addAttachment] = useMutation(CREATE_ATTACHMENT);

  const onSubmit = async ({ includedChildren, ...documents }) => {
    setLoading(true);
    const documentsToUpload = [];

    includedChildren.forEach(({ userId }) => {
      Object.entries(DOCS_TYPE).forEach(([key, type]) => {
        const inputKey = `${key}_${userId}`;
        const attachments = documents[inputKey] || [];
        attachments
          ?.filter((e) => !e.status)
          .forEach(({ name, contentType, base64 }) => {
            documentsToUpload.push({
              id: cuid(),
              subscription: { id: subscriptionId },
              user: { id: userId },
              name,
              content: base64,
              contentType,
              type,
            });
          });
      });
    });
    await Promise.all(
      documentsToUpload.map((document) =>
        addAttachment({ variables: { data: document } }),
      ),
    );
    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        subscription,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });
    onNext({
      payload: {
        fields: {
          ...get(project, "fields"),
          includedChildren,
        },
      },
    });
    setLoading(false);
  };

  return (
    <Template
      progress={currentStatus.progress}
      onBack={onBack}
      loading={loading || attachmentLoading || !initialValues}
      project={project}
      View={
        initialValues && initialValues.includedChildren.length > 0 ? (
          <View
            // key={JSON.stringify(initialValues)}
            locked={get(subscription, "subscription.locked", false)}
            onSubmit={onSubmit}
            initialValues={initialValues}
            translate={TRANSLATION}
            onBack={onBack}
          />
        ) : null
      }
      HeaderComponent={{
        subscription: get(subscription, "subscription"),
        tracer,
        project,
        contact: get(project, "contact"),
      }}
      contact={get(project, "contact")}
    />
  );
};
export default ChildrenJustification;
